import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../Translations/LenguageContext";
import Spinner from "../../../Components/Spinner";
import RotatingLogo from "../../../Loaders/RotatingLogo";
import TopicLoader from "../../../Loaders/TopicsLoader";
import { BsInfo } from "react-icons/bs";
import GeoTopicModal from "./GeoTopicModal";
import { PiPlus } from "react-icons/pi";
import NewGeoTopicModal from "./NewGeoTopicModal";
import ErrorMessage from "../../../Components/ErrorMessage";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const GeoTopics = ({
    account_id,
    org_id,
    geo_id,
    selectedContexts,
    selectedTopics,
    setSelectedTopics,
    saveTopicsGeo,
    setLoadingSave,
    loadingSave,
    errorMessage,
    setErrorMessage
}) => {
    const { t } = useLanguage()
    const navigate = useNavigate()
    const [loadingTopicsGen, setLoadingTopicsGen] = useState(false)
    const [topicsStatus, setTopicsStatus] = useState(null)
    const [geoTopics, setGeoTopics] = useState([])
    const [openGeoTopicModal, setOpenGeoTopicModal] = useState(false)
    const [openTopic, setOpenTopic] = useState({})
    const [openNewTopic, setOpeNewTopic] = useState(false)
    const maxTopics = 5

    const navigateBack = () => {
        setLoadingSave(false)
        setErrorMessage({ message: "", display: false });
        navigate(`/console/organizations/${org_id}/geo/${geo_id}/goal`);
    };

    const pollStatus = async () => {
        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/get_geo_topic_status`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        geo_id
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            if (result.data.topics_gen_status == "PENDING") {
                navigate(`/console/organizations/${org_id}/geo/${geo_id}/goal`)
            }

            if (result.data.topics_gen_status) {
                setTopicsStatus(result.data.topics_gen_status)
            }

        } catch (err) {
            console.error("Error fetching GEO optimization:", err);
        }
    };

    const getTopics = async () => {
        setLoadingTopicsGen(true);

        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/get_geo_topics`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        geo_id,
                        selected_contexts: selectedContexts
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            setGeoTopics(result.data)
            setSelectedTopics(result.selected_topics)

        } catch (err) {
            console.error("Error fetching GEO optimization:", err);
        } finally {
            setTimeout(() => {
                setLoadingTopicsGen(false);
            }, 500);
        }
    };

    const groupedTopics = geoTopics.reduce((groups, topic) => {
        const intent = topic.intent_type || "Other";

        if (!groups[intent]) {
            groups[intent] = [];
        }

        groups[intent].push(topic);

        return groups;
    }, {});

    const columns = [[], []];
    const heights = [0, 0];

    Object.entries(groupedTopics).forEach(([intent, topics]) => {
        const columnIndex = heights[0] <= heights[1] ? 0 : 1;
        columns[columnIndex].push({ intent, topics });
        const topicHeight = 50;
        const estimatedHeight = 90 + topics.length * topicHeight;
        heights[columnIndex] += estimatedHeight + 20;
    });

    const toggleTopic = (topicId) => {
        setSelectedTopics((prev) =>
            prev.includes(topicId)
                ? prev.filter((id) => id !== topicId)
                : [...prev, topicId].slice(-maxTopics)
        );
    };

    const handleTopicInfo = (e, topic) => {
        e.preventDefault();
        e.stopPropagation();
        openTopicInfo(topic);
    };

    const openTopicInfo = (topic) => {
        setOpenGeoTopicModal(true)
        setOpenTopic(topic)
    }

    useEffect(() => {
        pollStatus();
    }, [topicsStatus, account_id, org_id, geo_id]);

    useEffect(() => {
        const shouldPoll = ["QUEUED", "RUNNING"].includes(topicsStatus);

        if (!shouldPoll) return;

        const interval = setInterval(() => {
            pollStatus();
        }, 5000);

        return () => clearInterval(interval);
    }, [topicsStatus, account_id, org_id, geo_id]);

    useEffect(() => {
        if (topicsStatus == "COMPLETED") {
            getTopics();
        }
    }, [topicsStatus]);

    useEffect(() => {
        if (topicsStatus == "FAILED") {
            setTimeout(() => {
                navigateBack();
            }, 5500);
        }
    }, [topicsStatus]);

    if (!topicsStatus) {
        return (
            <div className="geo_context_cont fade-in">
                <RotatingLogo />
            </div>
        )
    }

    if (topicsStatus == "COMPLETED") {
        return (
            <div className="geo_context_cont">
                <p className="add_new_source_cont_title fade-in">
                    {t.select} {maxTopics} {t.geo.topics}
                </p>

                <p className="add_new_source_cont_desc fade-in">
                    {t.geo.geo_topics_description}
                </p>

                {loadingTopicsGen ? (
                    <TopicLoader />
                ) : (
                    <>
                        <div className="topics_wrapper fade-in">
                            {columns.map((column, index) => (
                                <div className="topics_column" key={index}>
                                    {column.map(({ intent, topics }) => (
                                        <div className="topics_container" key={intent}>
                                            <p className="topics_intent_title">
                                                {t.geo[intent]}
                                            </p>

                                            <div className="topics_container_inner">
                                                {topics.map((topic) => {
                                                    const isSelected = selectedTopics.includes(topic.id);

                                                    return (
                                                        <div
                                                            className={`topic_item ${isSelected ? "topic_item_selected" : ""} ${topic.id.startsWith("cus-") && "topic_item_custom"}`}
                                                            key={topic.id}
                                                            onClick={() => toggleTopic(topic.id)}
                                                        >
                                                            <p>{topic.name}</p>

                                                            <button
                                                                type="button"
                                                                className="topic_item_info"
                                                                onClick={(e) => handleTopicInfo(e, topic)}
                                                            >
                                                                <BsInfo />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                            <div className="add_custom_intent_type_cont">
                                <button className="add_custom_intent_type_cont_inner" onClick={() => setOpeNewTopic(true)}>
                                    <span>
                                        <PiPlus /> {t.geo.create_custom_topic}
                                    </span>
                                </button>
                            </div>
                        </div>
                        <p className="warning_source_message fade-in">
                            {t.geo.geo_topics_warning}
                        </p>
                    </>

                )}

                <div className="modal_footer_err_cont">
                    <ErrorMessage err={errorMessage} />
                </div>

                {!loadingTopicsGen && (
                    <div className="open_frame_body_footer">
                        <button
                            type="button"
                            className="modale_save_btn fade-in"
                            onClick={navigateBack}
                        >
                            <i className="fa-solid fa-chevron-left"></i>
                            {t.back}
                        </button>
                        {selectedTopics.length == 5 && (
                            <button
                                type="button"
                                className="modale_save_btn fade-in"
                                onClick={saveTopicsGeo}
                            >
                                {loadingSave ? (
                                    <Spinner loading={loadingSave} />
                                ) : (
                                    <>
                                        {t.next}
                                        <i className="fa-solid fa-chevron-right"></i>
                                    </>
                                )}
                            </button>

                        )}

                    </div>
                )}

                <GeoTopicModal
                    show={openGeoTopicModal}
                    handleClose={() => setOpenGeoTopicModal(false)}
                    openTopic={openTopic}
                />

                <NewGeoTopicModal
                    account_id={account_id}
                    org_id={org_id}
                    geo_id={geo_id}
                    show={openNewTopic}
                    handleClose={() => setOpeNewTopic(false)}
                    columns={columns}
                    selectedContexts={selectedContexts}
                    getTopics={getTopics}
                />
            </div>
        )
    }

    if (topicsStatus == "QUEUED" || topicsStatus == "RUNNING") {
        return (
            <div className="gen_topic_animation_container fade-in">
                <div className="gen_topic_animation_cont">
                    {topicsStatus == "QUEUED" ? (
                        <div className="gen_topic_cont_queued">
                            <p className="add_new_source_cont_title fade-in">
                                {t.geo.preparing_discovery_title}
                            </p>
                            <p className="add_new_source_cont_desc fade-in">
                               {t.geo.preparing_discovery_description}
                            </p>
                            <div className="gen_topic_animation_cont_queued">
                                <div className="geo_pulse fade-in">
                                    <span className="geo_pulse_ring" />
                                    <span className="geo_pulse_ring" />
                                    <span className="geo_pulse_ring" />
                                    <span className="geo_pulse_core" />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="gen_topic_cont_queued fade-in">
                            <p className="add_new_source_cont_title fade-in">
                                {t.geo.generating_topics_title}
                            </p>
                            <p className="add_new_source_cont_desc fade-in">
                                {t.geo.generating_topics_description}
                            </p>
                            <div className="gen_topic_animation_cont_running">
                                <div className="running_rectangles fade-in">
                                    <span className="running_rectangle" />
                                    <span className="running_rectangle" />
                                    <span className="running_rectangle" />
                                    <span className="running_rectangle" />
                                    <span className="running_rectangle" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    if (topicsStatus == "FAILED") {
        return (
            <div className="geo_context_cont">
                <div className="gen_topic_animation_cont">
                    <div className="gen_topic_cont_queued">
                        <p className="add_new_source_cont_title fade-in">
                            {t.geo.failed_title}
                        </p>
                        <p className="add_new_source_cont_desc fade-in">
                            {t.geo.failed_description}
                        </p>
                        <div className="gen_topic_animation_cont_failed" />
                    </div>
                </div>
            </div>
        )
    }
}

export default GeoTopics